import { reorder } from "../components/DraggableFilterList"
import type { FilterJoin } from "../utils/combineWithJoins"

const FALLBACK_DEFAULT_JOIN: FilterJoin = "or"

interface UseFilterListSectionOptions<T> {
  filters: T[]
  joins: FilterJoin[]
  max: number
  createDefault: () => T
  /**
   * Default join to use when adding a new filter or back-filling missing joins.
   * Single-speaker audio metrics override this to `"and"` so that adding a
   * time/turn range narrows within the speaker's audio rather than unioning.
   */
  defaultJoin?: FilterJoin
  /**
   * Called whenever the filters or joins change.
   * `nextJoins` is `undefined` when there are no joins to persist (zero or one filter)
   * so the caller can drop the field rather than serialize an empty array.
   */
  onChange: (nextFilters: T[], nextJoins: FilterJoin[] | undefined) => void
}

interface FilterListSectionHandlers<T> {
  handleAdd: () => void
  handleUpdate: (index: number, filter: T) => void
  handleRemove: (index: number) => void
  handleJoinChange: (joinIndex: number, next: FilterJoin) => void
  handleReorder: (from: number, to: number) => void
  belowCap: boolean
}

/**
 * Encapsulates add/update/remove/reorder/join-change bookkeeping for a single
 * include or exclude filter list. Used by both transcript and audio scope so
 * the join-array invariants stay aligned across the two surfaces.
 */
export function useFilterListSection<T>({
  filters,
  joins,
  max,
  createDefault,
  defaultJoin = FALLBACK_DEFAULT_JOIN,
  onChange,
}: UseFilterListSectionOptions<T>): FilterListSectionHandlers<T> {
  const emit = (nextFilters: T[], nextJoins: FilterJoin[]) => {
    onChange(nextFilters, nextJoins.length > 0 ? nextJoins : undefined)
  }

  const handleAdd = () => {
    if (filters.length >= max) {
      return
    }
    const nextFilters = [...filters, createDefault()]
    const nextJoins = filters.length === 0 ? joins : [...joins, defaultJoin]
    emit(nextFilters, nextJoins)
  }

  const handleUpdate = (index: number, filter: T) => {
    const nextFilters = [...filters]
    nextFilters[index] = filter
    emit(nextFilters, joins)
  }

  const handleRemove = (index: number) => {
    const nextFilters = filters.filter((_, position) => position !== index)
    // Drop the join immediately above the deleted row (or below if first row).
    const joinIndex = index === 0 ? 0 : index - 1
    const nextJoins = joins.filter((_, position) => position !== joinIndex)
    emit(nextFilters, nextJoins)
  }

  const handleJoinChange = (joinIndex: number, next: FilterJoin) => {
    const nextJoins = [...joins]
    while (nextJoins.length <= joinIndex) {
      nextJoins.push(defaultJoin)
    }
    nextJoins[joinIndex] = next
    emit(filters, nextJoins)
  }

  const handleReorder = (from: number, to: number) => {
    emit(reorder(filters, from, to), joins)
  }

  return {
    handleAdd,
    handleUpdate,
    handleRemove,
    handleJoinChange,
    handleReorder,
    belowCap: filters.length < max,
  }
}
